import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, PageProps } from 'gatsby'
import clsx from 'clsx'
import { useSetState } from 'ahooks'
import {
  Button,
  Card,
  Error,
  Form,
  Pagination,
  SEO,
  Space,
  Title,
} from 'components/common'
import {
  Booking,
  BookingsFilterForm,
  BookingsFilterFormValues,
  BookingsTable,
  ExportModal,
  ExportModalProps,
} from 'components/bookings'
import {
  OrderApproval,
  OrdersQuery,
  OrdersQueryResult,
  OrdersQueryVariables,
  ReportErrorCode,
  SortType,
  useOrdersExportMutation,
  useOrdersQuery,
} from 'lib/graphql/storeadmin'
import { usePagination } from 'lib/hooks'
import { date, datetime, time } from 'lib/str-formatter'
import { LineOutlined } from '@ant-design/icons'
import { downloadFile } from 'lib/common'

type ExportModalState = Pick<
  ExportModalProps,
  'okButtonProps' | 'cancelButtonProps' | 'open' | 'errors'
>

const defaultQueryVariables = {
  sort: { field: 'orderNumber', type: SortType.Desc },
}

export const Head = () => <SEO title="予約一覧" />

const IndexPage = ({ location }: PageProps<Queries.Query>): JSX.Element => {
  const [form] = Form.useForm<BookingsFilterFormValues>()
  const [searchVariable, setSearchVariable] = useState<OrdersQueryVariables>({
    approval: null,
    ...defaultQueryVariables,
  })

  const [ordersExport] = useOrdersExportMutation()
  const [exportModalState, setExportModalState] = useSetState<ExportModalState>(
    {
      open: false,
      errors: [],
    }
  )

  const {
    data,
    loading: initialLoading,
    error,
    goto,
    refetch,
    page,
    size,
    total,
    paginate,
  } = usePagination<OrdersQuery, OrdersQueryVariables, OrdersQueryResult>({
    initialSize: 10,
    queryOptions: {
      variables: { ...searchVariable },
    },
    queryHooks: useOrdersQuery,
  })

  const [loading, setLoading] = useState(false)
  useEffect(() => setLoading(initialLoading), [initialLoading])

  const dataSource = useMemo<Array<Booking>>(() => {
    return data?.orders?.edges?.reduce((result: Array<Booking>, { order }) => {
      const {
        id,
        approval,
        cancelReason,
        orderNumber,
        createdAt,
        customer,
        totalPrice,
        lineItems,
        bookingDates,
        period,
        numberOfPeople,
        status,
        updatedAt,
      } = order

      let bookedDate = date(bookingDates[0]?.date)
      if (bookedDate !== date(bookingDates[bookingDates?.length - 1].date)) {
        bookedDate = `${bookedDate} - ${date(
          bookingDates[bookingDates?.length - 1].date
        )}`
      }

      const orderStatus = status === 'DRAFT' ? '下書き' : '確定'

      const productNames = Array.from(
        new Set(lineItems?.edges?.map(({ lineItem }) => lineItem?.name?.jaJp))
      )

      result.push({
        key: id,
        approval,
        orderNumber,
        cancelReason: cancelReason,
        createdAt: datetime(createdAt),
        customer:
          customer?.lastName.includes('default_name') ||
          customer?.lastName.includes('default_name')
            ? '未登録'
            : `${customer?.lastName} ${customer?.firstName}`,
        total: totalPrice,
        groupName: customer?.corporationName || (
          <div className="text-center">
            <LineOutlined style={{ color: 'rgba(0,0,0,0.5)' }} />
          </div>
        ),
        bookingDate: bookedDate,
        bookingTime: `${time(period?.start)} - ${time(period?.end)}`,
        status: orderStatus,
        productNames: productNames,
        numberOfPeople,
        updatedAt: datetime(updatedAt),
      })
      return result
    }, [])
  }, [data])

  // Exportボタンがクリックされたときに呼び出される関数
  // エクスポートモーダルの状態を開くように設定する
  const onExportButtonClick = () => {
    setExportModalState({ open: true })
  }
  // フィルタリングが行われたときに呼び出される関数
  // フィルタリング条件に基づいて検索を実行する
  const onFiltering = async (variables: Partial<OrdersQueryVariables>) => {
    await handleSearch(variables)
  }
  // 検索を実行する関数
  // ローディング状態の設定、検索変数の更新、データの再取得を行う
  const handleSearch = async (variables: Partial<OrdersQueryVariables>) => {
    try {
      goto(1)
      setLoading(true)
      setSearchVariable({ ...searchVariable, ...variables })
      await refetch({ ...searchVariable, ...variables })
    } catch (error) {
      console.error('Error during search:', error)
    } finally {
      setLoading(false)
    }
  }
  // タブが切り替えられたときに呼び出される関数
  // タブに応じてフィルタリング条件を設定し、データを再取得する
  const onTabChange = async (
    key: 'all' | OrderApproval.Pending | OrderApproval.Approved
  ): Promise<void> => {
    setLoading(true)
    form.resetFields()
    goto(1)
    try {
      const variables = getTabVariables(key)
      setSearchVariable(variables)
      await refetch(variables)
    } catch (error) {
      console.error('Error during tab change:', error)
    } finally {
      setLoading(false)
    }
  }
  // タブに応じた検索変数を取得する関数
  // タブのキーに基づいて適切な検索変数を返す
  const getTabVariables = (
    key: 'all' | OrderApproval.Pending | OrderApproval.Approved
  ): OrdersQueryVariables => {
    switch (key) {
      case OrderApproval.Pending:
        return {
          approval: OrderApproval.Pending,
          ...defaultQueryVariables,
        }
      case OrderApproval.Approved:
        return {
          approval: OrderApproval.Approved,
          ...defaultQueryVariables,
        }
      case 'all':
      default:
        return { approval: null, ...defaultQueryVariables }
    }
  }
  // 検索条件をクリアする関数
  // クリア後のデフォルトの検索変数で検索を実行する
  const onSearchClear = async () => {
    const variables = {
      approval: searchVariable.approval,
      bookingDates: null,
      productName_Contains: null,
      customerQuery: null,
      ...defaultQueryVariables,
    }
    await handleSearch(variables)
  }
  // エクスポートモーダルのOKボタンがクリックされたときに呼び出される関数
  // エクスポートを実行し、成功時にはエクスポートURLを開く
  const onExportModalOk = useCallback<ExportModalProps['onOk']>(
    async (values) => {
      try {
        setExportModalState({
          okButtonProps: { loading: true },
          cancelButtonProps: { disabled: true },
        })

        const { data } = await ordersExport({
          variables: {
            input: {
              month: values.target.format('YYYY-MM-DD'),
            },
          },
        })

        const errors = data.reportOrdersExport.reportUserErrors
        if (errors.length > 0) {
          const errorMessages = errors.map(({ code }) => {
            switch (code) {
              case ReportErrorCode.NotFound:
                return '指定された期間内に該当するデータがありませんでした。'
              default:
                return 'エクスポート時に予期せぬエラーが発生しました。'
            }
          })
          setExportModalState({ errors: errorMessages })
        } else {
          const url = data.reportOrdersExport.url
          downloadFile(url)
          setExportModalState({ open: false, errors: [] })
        }
      } catch (e) {
        console.error('Error during export modal OK:', e)
        setExportModalState({
          errors: ['予期せぬエラーが発生しました。'],
        })
      } finally {
        setExportModalState({
          okButtonProps: { loading: false },
          cancelButtonProps: { disabled: false },
        })
      }
    },
    [setExportModalState, ordersExport]
  )
  // エクスポートモーダルのキャンセルボタンがクリックされたときに呼び出される関数
  // エクスポートモーダルを閉じる
  const onExportModalCancel = useCallback<ExportModalProps['onCancel']>(() => {
    setExportModalState({ open: false })
  }, [setExportModalState])

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <div className={clsx('flex', 'justify-between', 'items-center')}>
        <div>
          <Title level={1}>予約一覧</Title>
        </div>
        <div className={clsx('flex', 'justify-end')}>
          <Space direction="horizontal">
            <Button type="text" onClick={onExportButtonClick}>
              エクスポートする
            </Button>
            <Button type="primary">
              <Link to="./drafts/new">予約を作成する</Link>
            </Button>
            {/* )} */}
          </Space>
        </div>
      </div>

      <Card
        shadow
        tabList={[
          { key: 'all', tab: '全て' },
          { key: OrderApproval.Approved, tab: '承認済み' },
          { key: OrderApproval.Pending, tab: '承認待ち' },
        ]}
        defaultActiveTabKey={'all'}
        onTabChange={onTabChange}
      >
        <BookingsFilterForm
          onFiltering={onFiltering}
          onSearchClear={onSearchClear}
          form={form}
        />
        <Space direction="vertical" size="large">
          <BookingsTable dataSource={dataSource} loading={loading} />
          <Pagination
            current={page}
            pageSize={size}
            total={total}
            onChange={paginate}
            showSizeChanger
          />
        </Space>
      </Card>

      <ExportModal
        errors={exportModalState.errors}
        open={exportModalState.open}
        okButtonProps={exportModalState.okButtonProps}
        cancelButtonProps={exportModalState.cancelButtonProps}
        onOk={onExportModalOk}
        onCancel={onExportModalCancel}
      />
    </>
  )
}

export default IndexPage
